import { localizationInterpolation } from "@constructor/federation-common/base";
import { s as splitLanguage } from "./helpers.js";
function useFederationLocalization(options) {
  const loadingMap = /* @__PURE__ */ new Map();
  const fragmentLocalization = {
    getDir,
    getLanguage,
    t,
    onLanguageChange,
    interpolation: localizationInterpolation
  };
  const languageEventTarget = new EventTarget();
  const namespace = `fragment-${options.fragmentName}`;
  let language;
  let liveComponents = 0;
  return {
    fragmentLocalization,
    setLocalization,
    subscribe
  };
  function t(key, tOptions) {
    if (!options.i18next) {
      throw new Error("i18next is not configured in the root");
    }
    return options.i18next.t(key, { ...tOptions, ns: namespace });
  }
  function getDir() {
    if (!options.i18next) {
      throw new Error("i18next is not configured in the root");
    }
    return options.i18next.dir(language);
  }
  function getLanguage() {
    if (!language) {
      throw new Error("i18next is not configured in the root");
    }
    return language;
  }
  function onLanguageChange(callback) {
    if (!options.i18next) {
      throw new Error("i18next is not configured in the root");
    }
    const onChanged = () => callback(getLanguage());
    languageEventTarget.addEventListener("changed", onChanged);
    return () => languageEventTarget.removeEventListener("changed", onChanged);
  }
  function subscribe() {
    liveComponents++;
    return () => liveComponents--;
  }
  async function setLocalization() {
    if (!options.i18next) {
      return;
    }
    if (liveComponents === 0) {
      return;
    }
    const newLanguage = options.i18next.language;
    if (!loadingMap.has(newLanguage)) {
      const { languages } = options.i18next;
      const promise = loadLocalization(newLanguage, languages).catch((error) => {
        loadingMap.delete(newLanguage);
        throw error;
      });
      loadingMap.set(newLanguage, promise);
    }
    const existingLanguage = await loadingMap.get(newLanguage);
    if (existingLanguage) {
      setLanguage(existingLanguage);
    }
  }
  async function loadLocalization(newLanguage, languages) {
    const manifest = await options.fragmentNetwork.loadManifest();
    const finalLanguage = determineLanguage(manifest, newLanguage, languages);
    if (!finalLanguage) {
      return;
    }
    const resources = await options.fragmentNetwork.loadResources(finalLanguage);
    options.i18next.addResourceBundle(newLanguage, namespace, resources);
    return finalLanguage;
  }
  function determineLanguage(manifest, newLanguage, languages) {
    const { translations } = manifest;
    if (!translations) {
      return;
    }
    const existingLanguages = Object.keys(translations);
    if (!existingLanguages.length) {
      return;
    }
    let finalLanguage = getLookupLanguage(existingLanguages, languages);
    const { lang } = splitLanguage(newLanguage);
    if (finalLanguage) {
      if (lang === splitLanguage(finalLanguage).lang) {
        return finalLanguage;
      }
    }
    finalLanguage ?? (finalLanguage = existingLanguages[0]);
    for (const existingLanguage of existingLanguages) {
      if (new RegExp(`^${lang}\\b`, "ig").test(existingLanguage)) {
        finalLanguage = existingLanguage;
        break;
      }
      if (/^en\b/gi.test(existingLanguage)) {
        finalLanguage = existingLanguage;
      }
    }
    console.warn(
      `Language "${newLanguage}" isn't found in the fragment "${options.fragmentName}". Using "${finalLanguage}" instead.`
    );
    return finalLanguage;
  }
  function setLanguage(newLanguage) {
    language = newLanguage;
    languageEventTarget.dispatchEvent(new Event("changed"));
  }
  function getLookupLanguage(existingLanguages, lookupLanguages) {
    for (const existingLanguage of existingLanguages) {
      const existingNormalized = existingLanguage.toLowerCase().replace("_", "-");
      for (const lookupLanguage of lookupLanguages) {
        const lookupNormalized = lookupLanguage.toLowerCase().replace("_", "-");
        if (existingNormalized === lookupNormalized) {
          return existingLanguage;
        }
      }
    }
  }
}
export {
  useFederationLocalization as u
};
//# sourceMappingURL=fragmentLocalization.js.map
